import api from '../services/api';

const SUPPLIERS_URL = '/api/suppliers';

/**
 * Get paginated suppliers list with search and filters
 */
export const getSuppliers = async (page = 1, limit = 10, search = '', status = '') => {
  try {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString()
    });
    
    if (search.trim()) {
      params.append('search', search.trim());
    }
    
    if (status !== '') {
      params.append('status', status.toString());
    }

    const response = await api.get(`${SUPPLIERS_URL}?${params}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching suppliers:', error);
    throw error;
  }
};

/**
 * Get single supplier details
 */
export const getSupplier = async (id) => {
  try {
    const response = await api.get(`${SUPPLIERS_URL}/${id}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching supplier:', error);
    throw error;
  }
};

/**
 * Create new supplier
 */
export const createSupplier = async (supplierData) => {
  try {
    console.log('Creating supplier with data:', supplierData);
    const response = await api.post(SUPPLIERS_URL, supplierData);
    console.log('Create supplier response:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error creating supplier:', error);
    console.error('Error response:', error.response?.data);
    throw error;
  }
};

/**
 * Update supplier
 */
export const updateSupplier = async (id, supplierData) => {
  try {
    const response = await api.put(`${SUPPLIERS_URL}/${id}`, supplierData);
    return response.data;
  } catch (error) {
    console.error('Error updating supplier:', error);
    throw error;
  }
};

/**
 * Delete/deactivate supplier
 */
export const deleteSupplier = async (id) => {
  try {
    const response = await api.delete(`${SUPPLIERS_URL}/${id}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting supplier:', error);
    throw error;
  }
};

/**
 * Export suppliers to CSV
 */
export const exportSuppliers = async (search = '', status = '') => {
  try {
    const params = new URLSearchParams();
    
    if (search.trim()) {
      params.append('search', search.trim());
    }
    
    if (status !== '') {
      params.append('status', status.toString());
    }

    const response = await api.get(`${SUPPLIERS_URL}/export?${params}`, {
      responseType: 'blob'
    });
    
    // Create download link
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `suppliers_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    
    return { status: 'success', message: 'Export completed successfully' };
  } catch (error) {
    console.error('Error exporting suppliers:', error);
    throw error;
  }
};

/**
 * Import suppliers from CSV file
 */
export const importSuppliers = async (file) => {
  try {
    const formData = new FormData();
    formData.append('file', file);

    const response = await api.post(`${SUPPLIERS_URL}/import`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    
    return response.data;
  } catch (error) {
    console.error('Error importing suppliers:', error);
    throw error;
  }
};

/**
 * Get supplier groups
 */
export const getSupplierGroups = async () => {
  try {
    const response = await api.get(`${SUPPLIERS_URL}/groups`);
    return response.data;
  } catch (error) {
    console.error('Error fetching supplier groups:', error);
    throw error;
  }
};

/**
 * Get single supplier group by code
 */
export const getSupplierGroup = async (code) => {
  try {
    // URL encode the code to handle special characters like &, spaces, etc.
    const encodedCode = encodeURIComponent(code);
    const response = await api.get(`${SUPPLIERS_URL}/groups/${encodedCode}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching supplier group:', error);
    throw error;
  }
};

/**
 * Create new supplier group
 */
export const createSupplierGroup = async (groupData) => {
  try {
    console.log('Creating supplier group with data:', groupData);
    const response = await api.post(`${SUPPLIERS_URL}/groups`, groupData);
    console.log('Create supplier group response:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error creating supplier group:', error);
    console.error('Error response:', error.response?.data);
    throw error;
  }
};

/**
 * Update supplier group
 */
export const updateSupplierGroup = async (code, groupData) => {
  try {
    const response = await api.post(`${SUPPLIERS_URL}/groups/update`, { ...groupData, code });
    return response.data;
  } catch (error) {
    console.error('Error updating supplier group:', error);
    throw error;
  }
};

/**
 * Delete supplier group
 */
export const deleteSupplierGroup = async (code) => {
  try {
    // URL encode the code to handle special characters like &, spaces, etc.
    const encodedCode = encodeURIComponent(code);
    const response = await api.delete(`${SUPPLIERS_URL}/groups/${encodedCode}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting supplier group:', error);
    throw error;
  }
};

/**
 * Send COIDA compliance reminder emails
 */
export const sendCoidaReminders = async (bpCodes, subject, message) => {
  try {
    const response = await api.post(`${SUPPLIERS_URL}/coida-reminders`, {
      bpCodes,
      subject,
      message
    });
    return response.data;
  } catch (error) {
    console.error('Error sending COIDA reminders:', error);
    throw error;
  }
};

/**
 * Update supplier user status (activate/deactivate)
 */
export const updateSupplierUserStatus = async (supplierCode, userId, status) => {
  try {
    const response = await api.patch(`${SUPPLIERS_URL}/${supplierCode}/users/${userId}/status`, {
      status: status ? 1 : 0
    });
    return response.data;
  } catch (error) {
    console.error('Error updating supplier user status:', error);
    throw error;
  }
};

/**
 * Get supplier documents/attachments
 */
export const getSupplierDocuments = async (supplierCode) => {
  try {
    const response = await api.get(`${SUPPLIERS_URL}/${supplierCode}/documents`);
    return response.data;
  } catch (error) {
    console.error('Error fetching supplier documents:', error);
    throw error;
  }
};

/**
 * Get suppliers with non-compliant certificates by type (COIDA, BEE, or TAX)
 * @param {string} type - 'COIDA', 'BEE', or 'TAX'
 * @param {string} status - '0' or '1' for inactive/active
 */
export const getNonCompliantSuppliersByType = async (type = 'COIDA', status = '1') => {
  try {
    const params = new URLSearchParams({
      type: type.toUpperCase(),
      status
    });
    
    const response = await api.get(`${SUPPLIERS_URL}/compliance/by-type?${params}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching ${type} non-compliant suppliers:`, error);
    throw error;
  }
};

/**
 * Get all compliance data for the compliance dashboard
 * Returns suppliers with their status for COIDA, BEE, and TAX compliance
 * @param {string} status - '0' or '1' for inactive/active
 */
export const getComplianceData = async (status = '1') => {
  try {
    const params = new URLSearchParams({ status });
    
    const response = await api.get(`${SUPPLIERS_URL}/compliance/all?${params}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching compliance data:', error);
    throw error;
  }
};

/**
 * Get suppliers with non-compliant certificates by type (COIDA, BEE, or TAX)
 * Returns suppliers with missing, expired, or expiring soon certificates
 */
export const getNonCompliantSuppliers = async (status = '1') => {
  try {
    const params = new URLSearchParams();
    if (status) params.append('status', status);
    
    const response = await api.get(`${SUPPLIERS_URL}/non-compliant?${params}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching non-compliant suppliers:', error);
    throw error;
  }
};

/**
 * Send compliance reminder emails
 */
export const sendComplianceReminders = async (bpCodes, type, subject, message) => {
  try {
    const response = await api.post(`${SUPPLIERS_URL}/compliance/reminders`, {
      bpCodes,
      type,
      subject,
      message
    });
    return response.data;
  } catch (error) {
    console.error(`Error sending ${type} compliance reminders:`, error);
    throw error;
  }
};

export default {
  getSuppliers,
  getSupplier,
  createSupplier,
  updateSupplier,
  deleteSupplier,
  exportSuppliers,
  importSuppliers,
  getSupplierGroups,
  getSupplierGroup,
  createSupplierGroup,
  updateSupplierGroup,
  deleteSupplierGroup,
  sendCoidaReminders,
  getNonCompliantSuppliers,
  getNonCompliantSuppliersByType,
  getComplianceData,
  sendComplianceReminders,
  updateSupplierUserStatus,
  getSupplierDocuments
};
